import ko from 'knockout';
import VanillaContextMenu from 'vanilla-context-menu';
import { ICellRendererComp, ICellRendererParams } from 'ag-grid/main';
import { CompositeDisposable, SerialDisposable } from '@/gr/common/disposable';
import { InputDataDefinitionsStore, Cell, AggregateMetric } from '@/apps/timeSeriesViewer';
import { AggCellContextMenu } from './popovers/aggCellContextMenu';
import { Instance, Props as TippyProps } from 'tippy.js';
import cellTooltip from './popovers/cellTooltip';

export class InputDefinitionCellRenderer implements ITypedCellRendererComp<Cell, IInputDefinitionCellRendererParams> {
  private readonly _iconElement: HTMLElement;
  private readonly _disposable = new CompositeDisposable();
  private readonly _isSelectedSubscription = new SerialDisposable();
  private _params: (ITypedCellRendererParams<Cell> & IInputDefinitionCellRendererParams) | null = null;
  private contextMenu: VanillaContextMenu | undefined;
  private _tooltip: Instance<TippyProps> | undefined;

  constructor() {
    this._iconElement = document.createElement('div');
    this._iconElement.classList.add('cell');
    this.addEventListener(this._iconElement, 'click', this.onChanged.bind(this));
    this._disposable.add(this._isSelectedSubscription);
  }

  private addEventListener(element: HTMLElement, eventType: string, eventListener: (event: Event) => void) {
    element.addEventListener(eventType, eventListener);
    this._disposable.add(() => element.removeEventListener(eventType, eventListener));
  }

  init(params: ITypedCellRendererParams<Cell> & IInputDefinitionCellRendererParams): void {
    this.updateUi(params);
    this._tooltip ??= cellTooltip(this._iconElement, params.value?.inputDataDefinitionId, params.value?.isSelected);
  }

  getGui(): HTMLElement {
    return this._iconElement;
  }

  refresh(params: ITypedCellRendererParams<Cell> & IInputDefinitionCellRendererParams): boolean {
    this.updateUi(params);
    return true;
  }

  destroy(): void {
    this._tooltip?.destroy();
    this._disposable.dispose();
  }

  private updateUi(params: ITypedCellRendererParams<Cell> & IInputDefinitionCellRendererParams) {
    this._params = params;
    const cell = params.value;
    params.eGridCell.style.padding = '0';
    params.eGridCell.style.outline = 'none';
    if (cell) {
      this._iconElement.setAttribute('data-test-name', 'raw-data-item');
      this._iconElement.setAttribute('data-test-id', cell.inputDataDefinitionId.toUniqueString());
      this._isSelectedSubscription.setDisposable(
        ko.computed(() => {
          this.updateIsSelectedAppearance(cell.isSelected());
        })
      );
      this._iconElement.classList.add('selectable');

      if (cell.inputDataDefinitionId instanceof AggregateMetric) {
        this._iconElement.classList.remove('raw');
        this._iconElement.classList.add('aggregate');

        if (cell.inputDataDefinitionId.nonsensical) {
          this._iconElement.classList.add('nonsensical');
        }

        // arrow function is needed here to maintain 'this' in allChildren function
        this.contextMenu = AggCellContextMenu(this._iconElement, (toState?: boolean) => this.allChildren(toState));
      } else {
        params.eGridCell.style.backgroundColor = this.calculateBackgroundColor(cell.score);
        this._iconElement.classList.add('raw');
        this._iconElement.classList.remove('aggregate', 'nonsensical');

        this.contextMenu?.off();
      }
    } else {
      this._iconElement.classList.remove('selectable');
      this._tooltip?.destroy();
    }
  }

  private updateIsSelectedAppearance(isSelected: boolean) {
    isSelected ? this._iconElement.classList.add('selected') : this._iconElement.classList.remove('selected');
  }

  private calculateBackgroundColor(score: number) {
    const max = 255;
    const min = 249;
    const byte = Math.min(Math.max(Math.floor(min + (max - min) * score), min), max);
    return `rgb(${byte}, ${byte}, ${byte})`;
  }

  private onChanged() {
    if (this._params?.value) {
      const cell = this._params.value;
      const isSelected = !cell.isSelected();
      this.updateIsSelectedAppearance(isSelected);
      this._params.rawDataDefinitionsStore.setSelected(cell.inputDataDefinitionId, isSelected).then(() => this.updateIsSelectedAppearance(cell.isSelected()));
    }
  }

  private allChildren(toSate?: boolean) {
    if (this._params) {
      this.expandRow();

      switch (toSate) {
        case true:
          this._params.rawDataDefinitionsStore.addChildren(this._params.value.inputDataDefinitionId);
          break;
        case false:
          this._params.rawDataDefinitionsStore.removeChildren(this._params.value.inputDataDefinitionId);
          break;
        case undefined:
          this._params.rawDataDefinitionsStore.toggleChildren(this._params.value.inputDataDefinitionId);
          break;
      }
    }
  }

  private expandRow() {
    const row = this._iconElement.closest('.ag-row');
    if (row) {
      const rowIndex = row.getAttribute('row-index');
      // This finds the expander button corresponding to the row index and clicks it if isn't allready expanded.
      (row.closest('.ag-body')?.querySelector(`.ag-pinned-left-cols-container  > [row-index="${rowIndex}"] .ag-group-contracted:not(.ag-hidden)`) as HTMLElement | undefined)?.click();
    }
  }
}

export interface IInputDefinitionCellRendererParams {
  rawDataDefinitionsStore: InputDataDefinitionsStore;
}

interface ITypedCellRendererComp<TCell, TParams> extends ICellRendererComp {
  init(params: ITypedCellRendererParams<TCell> & TParams): void;
  refresh(params: ITypedCellRendererParams<TCell> & TParams): boolean;
}

interface ITypedCellRendererParams<T> extends ICellRendererParams {
  value: T;
}
